import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, InputAdornment, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DebounceInput } from 'react-debounce-input';

import EnhancedTable from '../../../../components/EnhancedTable';
import { EnhancedBody } from '../../components/EnhancedBody';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomSelect } from '../../../../components/CustomSelect';
import { CustomMultiSelect } from '../../../../components/CustomMultiSelect';

import { CustomInput } from '../../../../components/CustomInput';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { ReactComponent as SearchIcon } from '../../../../assets/images/search.svg';
import { ROUTES } from '../../../../constants/routes';
import { HEAD_CELLS } from '../../constants';
import { getListRoles } from '../../../../store/role/actions';
import { getCompanyBranches } from '../../../../store/company/actions';
import { getListUsers } from '../../../../store/user/actions';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS, STATUS_OPTIONS } from '../../../../constants/variables';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { getEventDeclensionForm } from '../../../../utils/getEventDeclension';
import { serviceSessionStorage } from '../../../../utils/serviceSessionStorage';

export const Team = () => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const userListState = useSelector((state) => state.userList);
  const { users: userList, fetching: fetchingUserList } = userListState;

  const roleListState = useSelector((state) => state.roleList);
  const { items: roleOptions, fetching: fetchingRoleOptions } = roleListState;

  const companyBranchesState = useSelector((state) => state.company);
  const { branches: branchesOptions, fetching: fetchingBranchesOptions } = companyBranchesState;

  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const { userData } = useSelector((state) => state.currentUser);

  const isNotVisibleByListOrSearchUsers = visibleBySectionPermission(permissions, [PERMISSIONS.USER.ListUsers.value, PERMISSIONS.USER.SearchUsers.value], 'not');
  const isNotVisibleByListOrFilterUsers = visibleBySectionPermission(permissions, [PERMISSIONS.USER.ListUsers.value, PERMISSIONS.USER.FilterUsers.value], 'not');
  const isUserInvitationDisabled = !visibleBySectionPermission(permissions, PERMISSIONS.USER.InviteUsers.value);
  const isAllowedToRetrieveUsers = visibleBySectionPermission(permissions, PERMISSIONS.USER.RetrieveUsers.value);
  const isAllowedToUpdateUsers = visibleBySectionPermission(permissions, PERMISSIONS.USER.UpdateUsers.value);
  const isAllowedToActivateUsers = visibleBySectionPermission(permissions, PERMISSIONS.USER.ActivateUsers.value);
  const isAllowedToDeactivateUsers = visibleBySectionPermission(permissions, PERMISSIONS.USER.DeactivateUsers.value);
  const isAllowedToFilterUsers = visibleBySectionPermission(permissions, PERMISSIONS.USER.FilterUsers.value);
  const isAllowedToListUsers = visibleBySectionPermission(permissions, PERMISSIONS.USER.ListUsers.value);
  const isPageVisible = visibleBySectionPermission(permissions, [
    PERMISSIONS.USER.ListUsers.value,
    PERMISSIONS.USER.CreateUsers.value,
  ], 'or');

  const currentPage = window.location.pathname;

  const [filter, setFilter] = useState({
    role: { id: 'all', label: 'Все' },
    branch: { id: 'all', label: 'Все' },
    orderBy: 'created_at',
    orderType: 'desc',
    status: isAllowedToFilterUsers ? [STATUS_OPTIONS[0], STATUS_OPTIONS[1]] : [],
    page: 0,
    size: serviceSessionStorage.getPaginationRows(currentPage) || 20,
    search: '',
  });

  useEffect(() => {
    if (isAllowedToListUsers) {
      dispatch(getListRoles());
      dispatch(getCompanyBranches());
    }
  }, [dispatch, isAllowedToListUsers]);

  useEffect(() => {
    if (isAllowedToListUsers) {
      dispatch(getListUsers(filter));
    } // eslint-disable-next-line
  }, [filter, isAllowedToListUsers]);

  useEffect(() => {
    if (!fetchingRoleOptions && roleOptions?.length) {
      roleOptions.unshift({
        id: 'all',
        label: 'Все'
      });
    } // eslint-disable-next-line
  }, [fetchingRoleOptions, roleOptions]);

  useEffect(() => {
    if (!fetchingBranchesOptions && branchesOptions?.length) {
      branchesOptions.unshift({
        id: 'all',
        label: 'Все'
      });
    } // eslint-disable-next-line
  }, [fetchingBranchesOptions, branchesOptions]);

  useEffect(() => {
    if (filter.branch && filter.role && filter.status.length === 3) {
       setFilter({ ...filter, status: [], page: 0 });
    }
  }, [filter]);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, [userList]);

  if (!Array.isArray(branchesOptions)) {
    return (
      <Box
        sx={{
          marginTop: '150px',
          textAlign: 'center'
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isPageVisible) {
    return <></>;
  }

  return (
    <Box>
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1" data-testid="Title">
        Команда
      </Typography>

      <Box display="flex" justifyContent="space-between" mb="24px">
        <CustomButton
          component={Link}
          to={ROUTES.TEAM_INVITE_PERSON}
          size="small"
          mr="24px"
          variant="contained"
          disabled={isUserInvitationDisabled}
          tooltip={isUserInvitationDisabled ? DISABLED_CONTROL_TOOLTIP : null}
          data-testid="InviteUser"
        >
          Пригласить
        </CustomButton>

        <Box display="flex" alignItems="center" flexWrap="wrap">
          <CustomSelect
            name="roleId"
            value={filter.role}
            onChange={(e) => setFilter({ ...filter, role: e, page: 0 })}
            variant="outlined"
            sx={{ width: 240, marginRight: '16px' }}
            size="small"
            options={roleOptions}
            startAdornmentText={
              <InputAdornment
                position="start"
                sx={{
                  marginRight: 0
                }}
              >
                <Typography component="span" variant="body1" color="text.primary">
                  Роль:
                </Typography>
              </InputAdornment>
            }
            loading={fetchingRoleOptions}
            disabled={fetchingUserList || isNotVisibleByListOrFilterUsers}
            tooltip={isNotVisibleByListOrFilterUsers ? DISABLED_CONTROL_TOOLTIP : null}
            data-testid="Role"
          />
          <CustomSelect
            name="branch"
            value={filter.branch}
            onChange={(e) => setFilter({ ...filter, branch: e, page: 0 })}
            variant="outlined"
            sx={{ width: 240, marginRight: '16px' }}
            size="small"
            options={branchesOptions}
            disabled={fetchingUserList || isNotVisibleByListOrFilterUsers}
            tooltip={isNotVisibleByListOrFilterUsers ? DISABLED_CONTROL_TOOLTIP : null}
            startAdornmentText={
              <InputAdornment
                position="start"
                sx={{
                  marginRight: 0
                }}>
                <Typography component="span" variant="body1" color="text.primary">
                  Филиал:
                </Typography>
              </InputAdornment>
            }
            data-testid="Branch"
          />

          <Box sx={{ width: 240, marginRight: '16px' }} data-testid="Status">
            <CustomMultiSelect
              name="status"
              value={filter.status}
              placeholder="Статус:"
              onChange={(e) => setFilter({ ...filter, status: e, page: 0 })}
              variant="outlined"
              options={STATUS_OPTIONS}
              disabled={isNotVisibleByListOrFilterUsers || fetchingUserList}
              tooltip={isNotVisibleByListOrFilterUsers ? DISABLED_CONTROL_TOOLTIP : null}
            />
          </Box>

          <DebounceInput
            minLength={2}
            debounceTimeout={500}
            onChange={(e) => setFilter({ ...filter, search: e.target.value, page: 0 })}
            element={(res) => (
              <CustomInput
                type="search"
                inputRef={searchInputRef}
                placeholder="Поиск"
                variant="outlined"
                value={filter.search}
                sx={{ width: 240 }}
                size="small"
                disabled={fetchingUserList || isNotVisibleByListOrSearchUsers}
                tooltip={isNotVisibleByListOrSearchUsers ? DISABLED_CONTROL_TOOLTIP : null}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                {...res}
              />
            )}
          />
        </Box>
      </Box>
      {isAllowedToListUsers?
        <>
          <Typography mb="24px" variant="subtitle2" component="p" sx={{ color: 'text.secondary' }} data-testid="Found">
            Найдено {getEventDeclensionForm(userList?.total, 'team')}
          </Typography>

          <Box data-testid="List">
            {!userList?.items?.length < 1 && (
              <EnhancedTable
                rows={userList?.items}
                headCells={HEAD_CELLS}
                filter={filter}
                total={userList?.total || 0}
                setFilter={setFilter}
                fetching={fetchingUserList}
                heightRow={77}>
                <EnhancedBody
                  rows={userList?.items}
                  branches={branchesOptions}
                  permissions={{
                    retrieve: isAllowedToRetrieveUsers,
                    update: isAllowedToUpdateUsers,
                    activate: isAllowedToActivateUsers,
                    deactivate: isAllowedToDeactivateUsers
                  }}
                  userId={userData.id}
                />
              </EnhancedTable>
            )}
            {fetchingUserList && (
              <Box
                sx={{
                  marginTop: '150px',
                  textAlign: 'center'
                }}>
                <CircularProgress />
              </Box>
            )}
            {!fetchingUserList && !!userList?.items?.length < 1 && <NotFoundItems />}
          </Box>
        </>
        : <NotFoundItems message="Вам запрещён доступ к просмотру списка менеджеров"/>}
    </Box>
  );
};
